import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import { InputTextarea } from '@/components/ui/inputs/input-textarea';
import { useForm, Controller } from 'react-hook-form';
import { getSettingService, updateSettingService } from '@/services/SettingServices';
import validationErrors from '@/lib/validationErrors';
import { useSelector, useDispatch } from 'react-redux';
import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import CardBody from '@/components/ui/cards/CardBody';
import PermissionCheck from '@/components/common/PermissionCheck';

const NotificationPage = ({ pageTitle }) => {
    const selectRef = useRef(null);
    const {
        register,
        setValue,
        getValues,
        formState: { errors },
        handleSubmit,
        control,
        reset,
    } = useForm();
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const [isFormSumbmit, setIsFormSumbmit] = useState(false);
    const filedList = [
        'notification_fcm_project_id',
        'notification_private_key_id',
        'notification_private_key',
        'notification_client_email',
        'notification_client_id',
        'notification_client_x509_cert_url',
        'notification_firebase_api_key',
        'notification_firebase_auth_domain',
        'notification_firebase_storage_bucket',
        'notification_firebase_messaging_sender_id',
        'notification_firebase_app_id',
        'notification_firebase_measurement_id',
        'notification_firebase_vapid_key',
    ];

    useEffect(() => {
        getDataByKey({
            key_list: filedList,
        });
    }, []);

    const formReset = async () => {
        try {
            var formData = getValues();
            for (const key in formData) {
                await setValue(key, '');
            }
            await reset();
        } catch (error) {
            console.error('Error in onReset:', error);
        }
    };

    const getDataByKey = (keyList) => {
        setIsLoading(true);
        dispatch(getSettingService(keyList))
            .then((res) => {
                var formData = res?.data?.data;
                for (const key in formData) {
                    if (formData.hasOwnProperty(key) && formData[key] != '') {
                        setValue(key, formData[key]);
                    } else {
                        setValue(key, '');
                    }
                }
                setIsLoading(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsLoading(false);
            });
    };

    const onSubmit = (data) => {
        setIsFormSumbmit(true);
        dispatch(updateSettingService(data))
            .then((res) => {
                getDataByKey({
                    key_list: filedList,
                });
                setIsFormSumbmit(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsFormSumbmit(false);
            });
    };

    return (
        <Card>
            <CardHeader isBorder={true}>
                <h3 className="db-card-title">{pageTitle}</h3>
            </CardHeader>
            <CardBody>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <Controller
                            control={control}
                            rules={{ required: true, minLength: 1 }}
                            name="notification_fcm_project_id"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    labelStyleClassName="db-field-title required"
                                    size="sm"
                                    labelText={'Firebase Project Id'}
                                    type="text"
                                    id="notification_fcm_project_id"
                                    errorData={{ minLength: '1' }}
                                    errorType={errors?.notification_fcm_project_id?.type}
                                />
                            )}
                        />

                        <Controller
                            control={control}
                            rules={{ required: true, minLength: 1 }}
                            name="notification_private_key_id"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    labelStyleClassName="db-field-title required"
                                    size="sm"
                                    labelText={'Firebase Private Key Id'}
                                    type="text"
                                    id="notification_private_key_id"
                                    errorData={{ minLength: '1' }}
                                    errorType={errors?.notification_private_key_id?.type}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            rules={{ required: true, minLength: 1 }}
                            name="notification_client_id"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    labelStyleClassName="db-field-title required"
                                    size="sm"
                                    labelText={'Firebase Client Id'}
                                    type="text"
                                    id="notification_client_id"
                                    errorData={{ minLength: '1' }}
                                    errorType={errors?.notification_client_id?.type}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            rules={{ required: true, minLength: 1 }}
                            name="notification_client_email"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    labelStyleClassName="db-field-title required"
                                    size="sm"
                                    labelText={'Firebase Client Email'}
                                    type="text"
                                    id="notification_client_email"
                                    errorData={{ minLength: '1' }}
                                    errorType={errors?.notification_client_email?.type}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            rules={{ required: true, minLength: 1 }}
                            name="notification_private_key"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    labelStyleClassName="db-field-title required"
                                    size="sm"
                                    labelText={'Firebase Private Key'}
                                    type="text"
                                    id="notification_private_key"
                                    errorData={{ minLength: '1' }}
                                    errorType={errors?.notification_private_key?.type}
                                />
                            )}
                        />

                        <Controller
                            control={control}
                            rules={{ required: true, minLength: 1 }}
                            name="notification_client_x509_cert_url"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    labelStyleClassName="db-field-title required"
                                    size="sm"
                                    labelText={'Firebase Client x509 Url'}
                                    type="text"
                                    id="notification_client_x509_cert_url"
                                    errorData={{ minLength: '1' }}
                                    errorType={errors?.notification_client_x509_cert_url?.type}
                                />
                            )}
                        />

                        <Controller
                            control={control}
                            rules={{ required: true, minLength: 1 }}
                            name="notification_firebase_api_key"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    labelStyleClassName="db-field-title required"
                                    size="sm"
                                    labelText={'Firebase API Key'}
                                    type="text"
                                    id="notification_firebase_api_key"
                                    errorData={{ minLength: '1' }}
                                    errorType={errors?.notification_firebase_api_key?.type}
                                />
                            )}
                        />

                        <Controller
                            control={control}
                            rules={{ required: true, minLength: 1 }}
                            name="notification_firebase_auth_domain"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    labelStyleClassName="db-field-title required"
                                    size="sm"
                                    labelText={'Firebase Auth Domain'}
                                    type="text"
                                    id="notification_firebase_auth_domain"
                                    errorData={{ minLength: '1' }}
                                    errorType={errors?.notification_firebase_auth_domain?.type}
                                />
                            )}
                        />

                        <Controller
                            control={control}
                            rules={{ required: true, minLength: 1 }}
                            name="notification_firebase_storage_bucket"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    labelStyleClassName="db-field-title required"
                                    size="sm"
                                    labelText={'Firebase Storage Bucket'}
                                    type="text"
                                    id="notification_firebase_storage_bucket"
                                    errorData={{ minLength: '1' }}
                                    errorType={errors?.notification_firebase_storage_bucket?.type}
                                />
                            )}
                        />

                        <Controller
                            control={control}
                            rules={{ required: true, minLength: 1 }}
                            name="notification_firebase_messaging_sender_id"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    labelStyleClassName="db-field-title required"
                                    size="sm"
                                    labelText={'Firebase Messaging Sender Id'}
                                    type="text"
                                    id="notification_firebase_messaging_sender_id"
                                    errorData={{ minLength: '1' }}
                                    errorType={errors?.notification_firebase_messaging_sender_id?.type}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            rules={{ required: true, minLength: 1 }}
                            name="notification_firebase_app_id"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    labelStyleClassName="db-field-title required"
                                    size="sm"
                                    labelText={'Firebase App Id'}
                                    type="text"
                                    id="notification_firebase_app_id"
                                    errorData={{ minLength: '1' }}
                                    errorType={errors?.notification_firebase_app_id?.type}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            rules={{ required: true, minLength: 1 }}
                            name="notification_firebase_measurement_id"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    labelStyleClassName="db-field-title required"
                                    size="sm"
                                    labelText={'Firebase Measurement Id'}
                                    type="text"
                                    id="notification_firebase_measurement_id"
                                    errorData={{ minLength: '1' }}
                                    errorType={errors?.notification_firebase_measurement_id?.type}
                                />
                            )}
                        />
                        <PermissionCheck permsission={'update_settings'}>
                            <div className="sm:col-span-2">
                                <button type="submit" className="db-btn text-white bg-primary" disabled={isFormSumbmit || isLoading ? true : false}>
                                    <i className="lab lab-save"></i>
                                    <span>Save</span>
                                    {isFormSumbmit ? (
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : (
                                        <></>
                                    )}
                                </button>
                            </div>
                        </PermissionCheck>
                    </div>
                </form>
            </CardBody>
        </Card>
    );
};

export default NotificationPage;
